
<script setup>
import { computed, onMounted, ref } from 'vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import { useStorage } from '@/composables/use-storage';
import TextInput from '@/components/forms/inputs/text-input.vue';
import SelectInput from '@/components/forms/inputs/select-input.vue';

const { addToast } = useToast();
const { hasPermission } = useStorage();

const flags = ref([]);

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const toggleFlag = async (flag) => {
    try {
        const response = await axios.post(`/api/flags/toggle-active/${flag.id}`);
        if (response.status === 201) {
            flag.active = !flag.active;
            addToast(`Flag ${flag.active ? 'activated' : 'deactivated'} successfully`, 'success');
        } else {
            addToast('Failed to update flag status', 'error');
        }
    } catch (error) {
        console.error('Error toggling flag:', error);
        addToast('Failed to update flag status', 'error');
    }
};



const fetchFlags = async () => {
    try {
        if (hasPermission('group-flags')) {
            const response = await axios.get('/api/flags');
            flags.value = response.data;
        }
    } catch (error) {
        console.error('Error fetching flags:', error);
        addToast('Failed to fetch flags', 'error');
    }
};

const filteredFlags = computed(() => {
    let filtered = flags.value;
    if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
        filtered = flags.value.filter(flag => 
            flag.name.toLowerCase().includes(query)
        );
    }
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredFlags.value.length / itemsPerPage.value);
});

const paginatedFlags = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredFlags.value.slice(start, end);
});

onMounted(async () => {
    await fetchFlags();
});
</script>

<template>

    <section class="p-5 bg-echoes-background-black">
        <UsersHeader>
            <template #title>Flags Management</template>
            <template #description>
                Manage flags for user accounts.
            </template>
        </UsersHeader>

         <!-- Stats Cards -->
         <div class="mt-6 grid grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-flag text-2xl text-sky-500"></i>
                </template>
                <template #title>Total Flags</template>
                <template #value>{{ flags.length }}</template>
            </StatCard>
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-flag-plus text-2xl text-emerald-500"></i>
                </template>
                <template #title>Active Flags</template>
                <template #value>{{ flags.filter(flag => flag.active).length }}</template>
            </StatCard>
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-flag-minus text-2xl text-amber-500"></i>
                </template>
                <template #title>Inactive Flags</template>
                <template #value>{{ flags.filter(flag => !flag.active).length }}</template>
            </StatCard>
        </div>
   

    <div class="py-6">
        <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light overflow-hidden">
            <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by username or email" 
                                class="w-full"
                            />
                        </div>
                        <div class="w-full sm:w-24">
                            <SelectInput
                                v-model="itemsPerPage"
                                :options="itemsPerPageOptions"
                                :hide-search="true"
                                @update:modelValue="currentPage = 1"
                            />
                        </div>
                    </div>
                </div>
            
        <div>
            <table class="min-w-full divide-y divide-echoes-border-light">
                <thead class="bg-echoes-border-light ">
                    <tr class="border-b border-echoes-border-light">
                        <th class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                            Name
                        </th>
                        <th class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                            Status
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                    <template v-for="flag in paginatedFlags" :key="flag.id">
                        <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                            <td class="px-6 py-4 text-sm font-medium text-white">
                                {{ flag.name }}
                            </td>
                            <td class="px-6 py-4 text-center text-sm">
                                <button
                                    @click="toggleFlag(flag)"
                                    :class="[
                                        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-echoes-primary/50',
                                        flag.active
                                            ? 'bg-echoes-primary'
                                            : 'bg-echoes-background-gray'
                                    ]"
                                >
                                    <span
                                        :class="[
                                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out',
                                            flag.active ? 'translate-x-6' : 'translate-x-1'
                                        ]"
                                    />
                                </button>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
             <!-- Pagination Footer -->
             <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredFlags.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredFlags.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="page in totalPages" :key="page">
                                        <button
                                            @click="currentPage = page"
                                            :class="[
                                                'px-3 py-1 rounded-lg text-sm font-medium',
                                                currentPage === page
                                                    ? 'bg-echoes-primary text-white'
                                                    : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                            ]"
                                        >
                                            {{ page }}
                                        </button>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
        </div>
    </div>
    </div>
</section>
<Toast />
</template>

<style scoped lang="postcss">

.table-container thead {
    @apply sticky top-0 z-10;
}

.table-container tbody {
    @apply relative;
}

.table-container::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.table-container::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

.table-container::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

.table-container::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.table-container tr {
    animation: fadeIn 0.3s ease-in-out;
}
</style> 